import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { conversationPath } from '@/config/routes';
import { formatClockTime } from '@/utils/datetime';
import { LOADING_VALUE } from '@/utils/placeholders';

import { ATTENTION_ALERTS_LIMIT } from './Dashboard.constants';
import { styles } from './Dashboard.styles';
import type { AlertRowProps, AttentionCardProps } from './Dashboard.types';

const AlertRow = ({ alert }: AlertRowProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'dashboard' });
	const deliveryFailed = alert.deliveries.some((delivery) => delivery.status === 'failed');
	const body = (
		<>
			<span className={styles.attentionTitle}>{alert.title}</span>
			<span className={styles.attentionTime}>{formatClockTime(alert.occurredAt)}</span>
			{deliveryFailed && (
				<span className={styles.attentionWarn}>
					<AlertTriangle className="h-[10px] w-[10px]" aria-hidden="true" />
					{t('ATTENTION_DELIVERY_FAILED')}
				</span>
			)}
		</>
	);

	if (!alert.conversationId) return <div className={styles.attentionItemStatic}>{body}</div>;

	return (
		<Link to={conversationPath(alert.conversationId)} className={styles.attentionItem}>
			{body}
		</Link>
	);
};

export const AttentionCard = ({ alerts }: AttentionCardProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'dashboard' });
	const items = alerts.data?.items;

	return (
		<section className={styles.card} aria-label={t('ATTENTION_TITLE')}>
			<h2 className={styles.sectionH}>{t('ATTENTION_TITLE')}</h2>
			{alerts.isError && <p className="text-[13px] text-error">{t('CELL_ERROR')}</p>}
			{!alerts.isError && !items && <p className={styles.sectionSub}>{LOADING_VALUE}</p>}
			{items?.length === 0 && <p className={styles.sectionSub}>{t('ALERTS_EMPTY')}</p>}
			{items && items.length > 0 && (
				<div className="flex flex-col gap-3xs">
					{items.slice(0, ATTENTION_ALERTS_LIMIT).map((alert) => (
						<AlertRow key={alert.id} alert={alert} />
					))}
				</div>
			)}
		</section>
	);
};
